import React, { type ReactNode } from 'react';

import { IntlProvider, useIntl } from 'react-intl';

import { Text } from '@atlaskit/primitives/compiled/text';

import commonCoreLazyI18nMessages from '../i18n/lazy';
import { getCodesFromLocale, useLazyI18nMessages } from './utils';

export const DEFAULT_LOCALE = 'en-US';

export type LazyI18nMessages = Record<
	string,
	(() => Promise<{ default: Record<string, string> }>) | undefined
>;

interface I18nMessages {
	[index: string]: Record<string, string> | undefined;
}

interface ProFormaIntlProviderProps {
	children: ReactNode;
	i18nMessages: I18nMessages;
	// lazyi18nMessages: LazyMessages TODO: Allow other packages to supply lazy messages.
	locale: string;
}

export const ProFormaIntlProvider: React.FC<ProFormaIntlProviderProps> = ({
	children,
	i18nMessages,
	locale,
}) => {
	// `commonCoreI18nMessages` is a temporary variable required only while we need to maintain
	// access to the 'proforma-common-core' translations for other packages. Once the translations
	// are properly per package, this can be removed.
	const commonCoreI18nMessages = useLazyI18nMessages(locale, commonCoreLazyI18nMessages);
	if (!commonCoreI18nMessages) {
		return null;
	}

	const { language, country } = getCodesFromLocale(locale);
	const messages =
		(country && i18nMessages[`${language}_${country}`]) ??
		i18nMessages[language] ??
		i18nMessages.en;
	// `aggregateMessages` is a temporary variable required only while we need to maintain
	// access to the 'proforma-common-core' translations for other packages. Once the translations
	// are properly per package, this can be removed.
	const aggregateMessages = {
		...commonCoreI18nMessages,
		...messages,
	};

	return (
		<IntlProvider
			messages={aggregateMessages}
			locale={country ? `${language}-${country}` : language}
			defaultLocale={DEFAULT_LOCALE}
			defaultRichTextElements={{
				b: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
				strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
			}}
		>
			{children}
		</IntlProvider>
	);
};

export const withProFormaIntlProvider =
	<Props extends Object>(
		i18nMessages: I18nMessages,
		WrappedComponent: React.ComponentType<Props>,
	): React.FC<Props> =>
	(props) => {
		const intl = useIntl();
		return (
			<ProFormaIntlProvider i18nMessages={i18nMessages} locale={intl.locale}>
				<WrappedComponent {...props} />
			</ProFormaIntlProvider>
		);
	};
